import { Component, Input } from "@angular/core";
import { CommonModule } from "@angular/common";

interface bulletPointFunctionReturn {
  hasDelimiter: boolean;
  boldText: string;
  normalText: string;
}

@Component({
  selector: "app-help-drawer-tab-content",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./help-drawer-tab-content.component.html",
  styleUrl: "./help-drawer-tab-content.component.scss",
})
export class HelpDrawerTabContentComponent {
  @Input() tabContent: any = {};

  /**
   * The bulletPoints content on the help-drawer-tab-content.html has some text formatting requirements that essentially needs the first chunk of text to be bolded
   * The main caveats are that they need to be bolded before the first instance of a dash (-) or a colon (:)
   * For example -> "Chicken: Wings, tenders, nuggets" -> "Chicken:" is bolded
   * Takes in the text string to format
   * Returns an object split up to more safely render content
   * hasDelimiter lets us know if a delimiter or separator exists (- or :)
   * boldText is the text that needs to be bolded ("Chicken:")
   * normalText is the rest of the text (" Wings, tenders, nuggets")
   **/
  formatBulletPointText(text: string): bulletPointFunctionReturn {
    // First, find if a dash or colon exists in the text and use the index to split the text
    // .indexOf will return -1 if a dash or colon doesn't exist
    const dashIndex = text.indexOf(" - ");
    const colonIndex = text.indexOf(": ");
    let splitIndex = -1;

    if (dashIndex !== -1 && colonIndex !== -1) {
      // For whatever reason if both dashIndex and colonIndex both simultaneously exist, we're going to bold the first instance (extremely unlikely scenario since we have control of the inputted content
      // Math.min() will grab the smaller index value (essentially whichever index comes first)
      splitIndex = Math.min(dashIndex, colonIndex);
    } else if (dashIndex !== -1) {
      splitIndex = dashIndex;
    } else if (colonIndex !== -1) {
      splitIndex = colonIndex;
    }

    // If splitIndex still equals -1 , then no instance of a delimiter (- or :) was found, so return the original inputted text
    if (splitIndex === -1) {
      return {
        hasDelimiter: false,
        boldText: "",
        normalText: text,
      };
    }

    // If we get here, then a delimiter (- or :) was found, so format the text accordingly
    // Split the text at the delimiter index + 1
    const boldText = text.substring(0, splitIndex + 2);
    const normalText = text.substring(splitIndex + 2);

    return {
      hasDelimiter: true,
      boldText: boldText,
      normalText: normalText,
    };
  }
}
